<template>
  <div id="jobsList" ref="jobsList" class="wrapper">
    <!-- eslint-disable vue/no-v-html -->
    <div class="jobs__intro">
      <p v-if="content.intro" class="jobs__text" v-html="content.intro"></p>
      <h2
        v-if="content.title"
        class="jobs__intro__title h2-custom"
        v-html="content.title"
      ></h2>
      <!-- <p v-if="content.subtitle" class="p-big" v-html="content.subtitle"></p> -->
    </div>

    <div class="jobs__list">
      <a
        v-for="(jobs, index) in content.items"
        :key="index"
        class="jobs__item"
        :href="jobs.url"
        @click.prevent="onClick(jobs.url)"
      >
        <p class="jobs__title" v-html="jobs.title"></p>
        <div class="jobs__infos">
          <p class="jobs__description" v-html="jobs.profile"></p>
          <p class="jobs__description" v-html="jobs.type"></p>
        </div>

        <SvgSprite symbol="ui-full-arrow" size="24" class="case__back__icon" />
      </a>
    </div>
    <p class="jobs__text--bottom p-medium" v-html="content.text"></p>
  </div>
  <transition name="fade" mode="out-in">
    <div
      ref="jobInfos"
      :class="{ actif: jobContent !== null }"
      class="pop-up"
      @keyup.esc="closeModal"
    >
      <div v-if="jobContent" class="pop-up__content">
        <div class="pop-up__header wrapper">
          <span>{{ t('job-offer') }}</span>
          <SvgSprite symbol="ui-cross" size="23" @click.prevent="closeModal" />
        </div>

        <JobContent :content="jobContent" :jobs="jobContent" />

        <div class="wrapper job__back">
          <button href="" @click.prevent="closeModal">
            <SvgSprite
              symbol="ui-full-arrow"
              size="24"
              class="case__back__icon"
            />
            {{ t('job-back-list') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { useAxios } from '@monofront/vue-axios-plugin'
import { ref, PropType } from 'vue'

import { onClickOutside } from '@vueuse/core'

import JobContent from '@/components/careers/JobContent.vue'

import { useChromeStore } from '@/stores/chrome'
import { getApiUrl } from '@/config/app'

interface JobsList {
  intro: string
  title: string
  subtitle: string
  text: string
  items: {
    title: string
    url: string
    profile: string
    type: string
  }[]
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface

defineProps({
  content: {
    type: Object as PropType<JobsList>,
    required: true,
  },
  careers: {
    type: Boolean,
  },
})

const jobContent = ref<typeof JobContent | null>(null)
const jobInfos = ref<HTMLElement>()

const axios = useAxios()
const { t } = useChromeStore()

let currentUrl: string | ''

onClickOutside(jobInfos, () => {
  closeModal()
})
const chrome = useChromeStore()
const locale = chrome.language

const onClick = async (url: string) => {
  try {
    const fullUrl = `${getApiUrl()}${url}`
    const { data } = await axios.get(fullUrl.replace(`/${locale}/`, '/'))
    document.body.classList.add('pop-upOpen')
    jobContent.value = data.content

    currentUrl = window.location.href
    history.pushState('url', '', url)
  } catch (error) {
    console.error(error)
  }
}
onClickOutside(jobInfos, () => {
  closeModal()
})

function closeModal() {
  document.body.classList.remove('pop-upOpen')
  jobContent.value = null
  history.pushState('currentUrl', '', currentUrl)
}
</script>

<style lang="scss" scoped>
.wrapper {
  @include fluid(
    padding-top,
    (
      xxs: 20px,
      xxl: 62px,
    )
  );

  border-top: 1px solid var(--c-foreground);
}

:deep {
  hr {
    background-color: var(--c-background);
  }

  .cta-outer {
    .cta__headline {
      color: var(--c-background);
    }

    .cta__word {
      // prettier-ignore
      background-image: linear-gradient(var(--c-background), var(--c-background));
    }
  }

  .job__back {
    // prettier-ignore
    @include fluid(padding-top,(xxs: 40px,xxl: 80px,));

    // prettier-ignore
    @include fluid(padding-bottom,(xxs: 40px,xxl: 80px,));

    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      transform: rotate(180deg);
      margin-right: 12px;
    }

    a,
    button {
      display: flex;
      justify-content: center;
      align-items: center;
      color: inherit;
      font-family: $ff-alt;
      font-weight: 900;
    }

    button {
      background-color: transparent;
      border: none;
      cursor: pointer;
    }
  }

  .wysiwyg li::before {
    background-color: var(--c-background) !important;
  }
}

.jobs__intro {
  &__title {
    margin: 40px 0;

    @include mq(m) {
      margin: 62px 0 88px;
    }
  }
}

.jobs__list {
  margin-top: 72px;
}

.jobs__item {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 30px 0;
  color: var(--c-foreground);
  border-top: 1px solid var(--c-foreground);
  transition: all 0.2s ease-in-out;

  &:last-of-type {
    border-bottom: 1px solid var(--c-foreground);
  }

  * {
    transition: all 0.2s ease-in-out;
    pointer-events: none;
  }

  svg {
    fill: var(--c-foreground);

    @include mq($until: l) {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 0;
    }
  }

  &:hover {
    padding: 30px 20px;
    background-color: var(--c-foreground);
    cursor: pointer;

    * {
      color: var(--c-background);
    }

    svg {
      fill: var(--c-background);
      right: 20px;
    }
  }

  @include mq($until: l) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.jobs__text {
  &--bottom {
    margin: auto;
    padding: 70px 0;
    text-align: center;

    @include mq(l) {
      width: col(8);
    }

    @include mq(xxl) {
      width: col(6);
    }
  }
}

.jobs__title {
  font-size: 30px;
  line-height: 30px;

  @include mq(l) {
    position: absolute;
    // width: 40%;
  }

  @include mq($until: l) {
    width: 75%;
    margin-bottom: 20px;
  }
}

.jobs__infos {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  p {
    &:not(:last-child) {
      @include mq($until: l) {
        margin-right: 20px;
      }
    }
  }

  @include mq(l) {
    width: 30%;
    margin-left: 61%;
  }

  @include mq(xl) {
    width: 40%;
    margin-left: 48%;

    p {
      width: 40%;
    }
  }
}

.jobs__description {
  font-size: 12px;
  text-transform: uppercase;
}

.pop-up {
  position: fixed;
  z-index: 99999;
  top: 0;
  right: 0;
  overflow-y: scroll;
  width: 100%;
  height: 100%;
  color: var(--c-background);
  background-color: var(--c-foreground);
  // padding: 80px;
  touch-action: none;
  box-shadow: 0 0 100px 100px rgba(45, 27, 33, 50%);
  opacity: 0;
  transform: translateX(100%);
  transition: all 0.2s ease-in-out;
  cursor: auto;
  pointer-events: none;

  &__content {
    cursor: auto;
  }

  &.actif {
    transform: translateX(0);
    opacity: 1;
    pointer-events: all;
    touch-action: auto;
  }

  @include mq(l) {
    width: 80%;
  }

  @include mq(xxxl) {
    padding: 0 80px;
  }

  :deep {
    .cta-rounded-outer--big {
      svg {
        .icon {
          fill: var(--c-foreground);
        }

        .circle {
          fill: var(--c-background);
          // var(--c-background)
        }

        .text {
          fill: var(--c-foreground);
        }
      }

      @include mq($until: nav) {
        right: 0;

        svg {
          width: 10rem;
          height: 10rem;
        }
      }
    }

    .info-value,
    .info-label {
      span {
        color: var(--c-background) !important;
      }
    }

    .job__back__content {
      display: none;
    }

    .polaroid {
      background-color: var(--c-foreground);
      border-color: var(--c-background);
    }

    .polaroid__caption {
      color: var(--c-background);
    }
  }
}

.pop-up__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid var(--c-background);

  svg {
    cursor: pointer;
  }

  span {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 5px;
  }
}
</style>
